import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useWatch } from 'react-hook-form'
import {
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@the-platform-group/component-library'
import { Select } from '@the-platform-group/component-library/Select'
import { AGENTIC_PROMPT_DEFAULT_STATUS, AGENTIC_PROMPT_STATUSES } from 'constants/agenticPrompts'
import { usePromptMetadata } from 'hooks/usePromptMetadata'
import { useAgenticMetadata } from 'hooks/useAgenticMetadata'

import ParentPromptSelect from './ParentPromptSelect'

const statusOptions = AGENTIC_PROMPT_STATUSES.map(status => ({
  value: status,
  label: status,
}))

/**
 * Renders the editable fields for an agentic prompt.
 * @param {object} props - component props
 * @param {object} props.control - react-hook-form control object
 * @param {object} props.formState - react-hook-form formState object
 * @param {string} [props.promptId] - id of the prompt being edited (used to exclude it from the parent dropdown)
 * @returns {React.ReactElement} the form fields
 */
const PromptFormFields = ({ control, formState, promptId }) => {
  const { errors } = formState
  const isChild = useWatch({ control, name: 'isChild' })
  const body = useWatch({ control, name: 'body' })
  const bodyInputRef = useRef(null)
  const [selectorToInsert, setSelectorToInsert] = useState('')

  /**
   * Splices a `{{name}}` placeholder into the prompt body at the caret (replacing any
   * selected text), then restores focus with the caret just after the inserted text.
   * The dropdown is reset so the same selector can be picked twice in a row.
   * @param {string} name - selector type name, e.g. "date_range"
   * @param {object} field - react-hook-form field for `body`
   */
  const insertSelector = (name, field) => {
    const placeholder = `{{${name}}}`
    const input = bodyInputRef.current
    const currentBody = field.value || ''
    const hasSelection = input && (input.selectionStart !== 0 || input.selectionEnd !== 0)
    const start = hasSelection ? input.selectionStart : currentBody.length
    const end = hasSelection ? input.selectionEnd : currentBody.length

    field.onChange(`${currentBody.slice(0, start)}${placeholder}${currentBody.slice(end)}`)
    setSelectorToInsert('')

    if (input) {
      const caret = start + placeholder.length
      // Wait for the controlled value to flush before moving the caret
      window.requestAnimationFrame(() => {
        input.focus()
        input.setSelectionRange(caret, caret)
      })
    }
  }

  const { workflowOptions, appDomainOptions } = usePromptMetadata()
  const {
    selectorTypeOptions,
    findUnknownSelectors,
    isLoading: isMetadataLoading,
  } = useAgenticMetadata()

  // Warn only — bodies may legitimately contain non-selector braces, and blocking
  // save would make existing prompts uneditable.
  const unknownSelectors = findUnknownSelectors(body || '')
  const unknownSelectorWarning = unknownSelectors.length
    ? `Unknown ${unknownSelectors.length === 1 ? 'selector' : 'selectors'}: ${unknownSelectors
        .map(name => `{{${name}}}`)
        .join(', ')}. They will not render as chips.`
    : null

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormLabel htmlFor="title">Title</FormLabel>
        <Controller
          name="title"
          control={control}
          rules={{ required: 'Title is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              id="title"
              fullWidth
              variant="outlined"
              {...(errors.title && { error: true })}
              helperText={errors.title?.message}
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel htmlFor="workflow">Workflow</FormLabel>
        <Controller
          name="workflow"
          control={control}
          rules={{ required: 'Workflow is required' }}
          render={({ field }) => (
            <Select
              id="workflow"
              fullWidth
              options={workflowOptions}
              value={field.value || ''}
              onChange={event => field.onChange(event.target.value)}
              hasError={!!errors.workflow}
              helperText={errors.workflow?.message}
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel sx={{ my: 1 }} htmlFor="body">
          Prompt body
        </FormLabel>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Write the prompt text here. Insert <code>{'{{selector}}'}</code> placeholders to add
          interactive fields (e.g., <code>{'{{date_range}}'}</code>, <code>{'{{person}}'}</code>,{' '}
          <code>{'{{tag}}'}</code>) that users can fill in when using the prompt. To request a new
          selector type, contact product — new selector types require frontend development work.
        </Typography>
        <Controller
          name="body"
          control={control}
          rules={{ required: 'Prompt body is required' }}
          render={({ field }) => (
            <>
              <Select
                id="insertSelector"
                fullWidth
                label="Insert a selector"
                options={selectorTypeOptions}
                value={selectorToInsert}
                onChange={event => insertSelector(event.target.value, field)}
                disabled={isMetadataLoading}
                sx={{ mt: 1 }}
              />
              <TextField
                {...field}
                id="body"
                inputRef={bodyInputRef}
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                {...(errors.body && { error: true })}
                helperText={errors.body?.message || unknownSelectorWarning}
                sx={{ mt: 1 }}
              />
            </>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel htmlFor="instructions">Instructions</FormLabel>
        <Typography variant="body2" color="textSecondary">
          Appended to the agent&rsquo;s context when this prompt is invoked. Not shown to users.
        </Typography>
        <Controller
          name="instructions"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="instructions"
              fullWidth
              multiline
              minRows={4}
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel htmlFor="categories">App Domains</FormLabel>
        <Controller
          name="categories"
          control={control}
          rules={{
            validate: value =>
              (value && value.length > 0) || 'Please select at least one app domain',
          }}
          render={({ field }) => (
            <Select
              id="categories"
              fullWidth
              isMulti
              options={appDomainOptions}
              value={field.value || []}
              onChange={event => field.onChange(event.target.value)}
              hasError={!!errors.categories}
              helperText={errors.categories?.message}
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel htmlFor="status">Status</FormLabel>
        <Typography variant="body2" color="textSecondary">
          Controls whether this prompt is available to users in the live application.
        </Typography>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Select
              id="status"
              fullWidth
              options={statusOptions}
              value={field.value || AGENTIC_PROMPT_DEFAULT_STATUS}
              onChange={event => field.onChange(event.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2">Hierarchy</Typography>
        <Controller
          name="isChild"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <FormControlLabel
              label="This is a child prompt"
              control={
                <Checkbox
                  {...field}
                  checked={!!value}
                  onChange={event => onChange(event.target.checked)}
                />
              }
            />
          )}
        />
      </Grid>

      {isChild && (
        <Grid item xs={12}>
          <Controller
            name="parentPrompt"
            control={control}
            shouldUnregister
            rules={{
              validate: value => !!value?.id || 'Parent prompt is required for child prompts',
            }}
            render={({ field: { value, onChange } }) => (
              <ParentPromptSelect
                value={value}
                onChange={onChange}
                excludeId={promptId}
                error={!!errors.parentPrompt}
                helperText={errors.parentPrompt?.message}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  )
}

PromptFormFields.propTypes = {
  control: PropTypes.object.isRequired,
  formState: PropTypes.object.isRequired,
  promptId: PropTypes.string,
}

export default PromptFormFields
